import React, { useCallback, useState } from 'react';
import StorageSelector from '../../../components/storage/StorageSelector';
import ArticleSelector from '../../../components/article/ArticleSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { usePostStockTransferMutation } from '../../../redux/api/stockApi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useApiErrorToast } from '../../../hooks/useApiError';

const StockTransfer = () => {
  const [fromStorage, setFromStorage] = useState(null);
  const [toStorage, setToStorage] = useState(null);
  const [reason, setReason] = useState('');
  const [articlesToBook, setArticlesToBook] = useState([]);
  const [postTransfer, { isLoading }] = usePostStockTransferMutation();
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();

  const handleSetArticlesToBook = useCallback((newArticles) => {
    setArticlesToBook(newArticles);
  }, []);

  const removeLine = (indexToRemove) => {
    setArticlesToBook((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleChangeQuantity = (event, index) => {
    const newQuantity = parseInt(event.target.value) || 0;
    setArticlesToBook((prev) => {
      const updated = [...prev];
      updated[index].quantity = newQuantity;
      return updated;
    });
  };

  const sameStorage = !!fromStorage && fromStorage === toStorage;
  const validLines = articlesToBook.filter((line) => line.quantity > 0);
  const canSubmit =
    !!fromStorage && !!toStorage && !sameStorage && validLines.length > 0;

  const handleSubmit = async () => {
    const postingObj = {
      from_storage_id: fromStorage,
      to_storage_id: toStorage,
      reason: reason.trim(),
      items: validLines.map((line) => ({
        article_id: line.article.id,
        quantity: line.quantity,
      })),
    };
    try {
      await postTransfer(postingObj).unwrap();
      toast.success('Umlagerung gebucht!');
      navigate('/stock');
    } catch (err) {
      apiErrorToast(err, 'Fehler beim Buchen der Umlagerung');
    }
  };

  return (
    <div>
      <h3>Umlagerung</h3>
      <p className='text-muted'>
        Bestand von einem Lagerort auf einen anderen umbuchen.
      </p>

      <div className='row mt-4'>
        <div className='col-3'>
          <label htmlFor='fromStorage' className='col-form-label'>
            Von Lagerort
          </label>
        </div>
        <div className='col-3'>
          <StorageSelector
            storageLocation={fromStorage}
            setStorageLocation={setFromStorage}
          />
        </div>
      </div>

      <div className='row mt-3'>
        <div className='col-3'>
          <label htmlFor='toStorage' className='col-form-label'>
            Nach Lagerort
          </label>
        </div>
        <div className='col-3'>
          <StorageSelector
            storageLocation={toStorage}
            setStorageLocation={setToStorage}
          />
        </div>
      </div>

      {sameStorage ? (
        <div className='row mt-2'>
          <div className='col-6 offset-3'>
            <div className='text-danger'>
              Quell- und Ziellager müssen unterschiedlich sein.
            </div>
          </div>
        </div>
      ) : null}

      <div className='row mt-3'>
        <div className='col-3'>
          <label htmlFor='reason' className='col-form-label'>
            Grund (optional)
          </label>
        </div>
        <div className='col-6'>
          <input
            id='reason'
            type='text'
            className='form-control'
            value={reason}
            maxLength={255}
            placeholder='z.B. Umverteilung Vorlauf'
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>

      <div className='row mt-3'>
        <div className='col-3'>
          <label htmlFor='article' className='col-form-label'>
            Artikel hinzufügen
          </label>
        </div>
        <div className='col-3'>
          <ArticleSelector
            articlesToBook={articlesToBook}
            setArticlesToBook={handleSetArticlesToBook}
          />
        </div>
      </div>

      <div className='mt-5'></div>
      {articlesToBook.length > 0 ? (
        <>
          <h4>Umzulagernde Artikel:</h4>
          <table className='table table-striped mt-3 align-middle'>
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>Artikel</th>
                <th>Gebinde</th>
                <th>Menge</th>
              </tr>
            </thead>
            <tbody>
              {articlesToBook.map((line, index) => (
                <tr key={index}>
                  <td>
                    <button
                      className='btn btn-sm ps-0 text-danger border-0'
                      onClick={() => removeLine(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                  <td>{line.article.name}</td>
                  <td>{line.article.unit.display_name}</td>
                  <td>
                    <input
                      className='form-control'
                      type='number'
                      min={1}
                      value={line.quantity}
                      onChange={(event) => handleChangeQuantity(event, index)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            className='btn btn-primary'
            onClick={handleSubmit}
            disabled={isLoading || !canSubmit}
          >
            Umlagerung buchen
          </button>
        </>
      ) : null}
    </div>
  );
};

export default StockTransfer;
